import { Injectable, inject, signal } from '@angular/core';
import { Auth, signInWithCustomToken, signOut } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { LoggerService } from '@core/services/infra/logger.service';

interface ImpersonateUserResponse {
  customToken: string;
  expiresAt: number;
  target: {
    uid: string;
    email: string | null;
    displayName: string | null;
  };
}

interface ImpersonationContext {
  /** UID of the admin who initiated the impersonation. */
  adminUid: string;
  adminEmail: string | null;
  /** Target user being impersonated. */
  targetUid: string;
  targetEmail: string | null;
  targetDisplayName: string | null;
  /** Epoch ms when the custom token expires (15 min after start). */
  expiresAt: number;
}

const STORAGE_KEY = 'proporio.impersonation.context';

/**
 * Manages the impersonation lifecycle :
 *   1. `start(targetUid, reason)` calls the `impersonateUser` callable,
 *      receives a custom token, stashes the admin's identity, signs in
 *      as the target.
 *   2. While impersonating, `context()` returns the active session info
 *      so the persistent banner can display "Impersonating X — Exit".
 *   3. `stop()` wipes storage and returns the admin to their own session.
 *
 * Storage uses sessionStorage : impersonation must NOT survive a tab
 * close / browser restart. The 15 min `expiresAt` is a client-side ceiling
 * enforced by the banner, not by the server.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);
  private readonly logger = inject(LoggerService);

  private readonly contextSignal = signal<ImpersonationContext | null>(this.loadFromStorage());

  /** Read-only signal so components can react to start/stop. */
  readonly context = this.contextSignal.asReadonly();

  /** Convenience for templates : `if (impersonation.isActive())`. */
  isActive(): boolean {
    return this.contextSignal() !== null;
  }

  /**
   * Begin an impersonation session. The current admin user MUST be a
   * superadmin (the backend callable enforces this). The current user
   * is captured BEFORE the sign-in switch so we know who to attribute
   * the session to.
   */
  async start(targetUid: string, reason: string): Promise<void> {
    const adminUser = this.auth.currentUser;
    if (!adminUser) {
      throw new Error('No active admin session — sign in first');
    }

    const callable = httpsCallable<
      { targetUserId: string; reason: string },
      ImpersonateUserResponse
    >(this.functions, 'impersonateUser');
    const { data } = await callable({ targetUserId: targetUid, reason });

    // Stash admin identity BEFORE we sign-in-with-custom-token (which
    // replaces auth.currentUser).
    const ctx: ImpersonationContext = {
      adminUid: adminUser.uid,
      adminEmail: adminUser.email,
      targetUid: data.target.uid,
      targetEmail: data.target.email,
      targetDisplayName: data.target.displayName,
      expiresAt: data.expiresAt,
    };
    this.persistToStorage(ctx);
    this.contextSignal.set(ctx);

    this.logger.info(
      'ImpersonationService',
      `Switching session: admin=${ctx.adminEmail} → target=${ctx.targetEmail}`,
    );

    // Now switch the Firebase auth session.
    await signInWithCustomToken(this.auth, data.customToken);
  }

  /**
   * End the impersonation session and sign the admin back in through the
   * `stopImpersonation` callable (no admin credentials are cached
   * client-side). If that fails, the session is signed out and the admin
   * re-authenticates via the auth shell.
   */
  async stop(): Promise<'admin' | 'signedOut'> {
    // Ask the backend for a token back into the admin session (it audits the
    // end too). Any failure — expired session, revoked admin — falls back to
    // a plain sign-out so the target session never lingers.
    let adminToken: string | null = null;
    try {
      const callable = httpsCallable<Record<string, never>, { customToken: string }>(
        this.functions,
        'stopImpersonation',
      );
      adminToken = (await callable({})).data.customToken;
    } catch (error: unknown) {
      this.logger.logError('ImpersonationService', 'stopImpersonation failed, signing out', error);
    }

    this.clearStorage();
    this.contextSignal.set(null);
    if (adminToken) {
      await signInWithCustomToken(this.auth, adminToken);
      return 'admin';
    }
    await signOut(this.auth);
    return 'signedOut';
  }

  /**
   * Called periodically by the banner component to evict expired
   * sessions. After 15 min the custom token is invalid anyway.
   */
  checkExpiration(): boolean {
    const ctx = this.contextSignal();
    if (!ctx) return false;
    if (Date.now() >= ctx.expiresAt) {
      void this.stop();
      return true;
    }
    return false;
  }

  private loadFromStorage(): ImpersonationContext | null {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const ctx = JSON.parse(raw) as ImpersonationContext;
      if (Date.now() >= ctx.expiresAt) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return ctx;
    } catch {
      return null;
    }
  }

  private persistToStorage(ctx: ImpersonationContext): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  }

  private clearStorage(): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(STORAGE_KEY);
  }
}
